import React, { useEffect } from 'react';
import { Head, Response } from '@shared/index';
import { getMainImage, UTMBuilder } from '../../../../../Shared/utils';
import {
  Container,
  ContainerData,
  ContainerHeader,
  Title,
} from './ResultsApplicationScreen.styled';
import Image from 'next/image';
import { BoxActionCompact } from '@aptuno/aptuno-ui';
import {
  EstateLegalId,
  EstateApplication,
} from '@contexts/Estate/EstateApplication/domain';
import { useRouter } from 'next/router';
import { useEstateApplicationActions } from '../../store';
import { EstateDetails } from '@contexts/Estate/Shared';
import { useLoadEstatesHook } from './use-load-estates-hook';
import {
  EstateApplicationResultItem,
  EstateApplicationResultItemResult,
  EstateApplicationResultItemResultTranslate,
  getEstateApplicationResult,
} from './result-list-item';

const constants = {
  ESTATE_APPLICATION_RESULTS_PAGE_URL:
    '/estate-application/results-application',
  UTM_PARAMS: {
    UTM_SOURCE: 'application_results',
    UTM_MEDIUM: 'aptuno_web_ssr',
    UTM_CAMPAIGN: 'complete_application_from_aptuno',
  },
};

export const ResultsApplicationScreen = () => {
  const { result: userEstateApplications, get: getUserEstateApplications } =
    useEstateApplicationActions().estateApplciationQueryActions.useGetMyEstateApplications();

  const visibleApplications = React.useMemo(
    () =>
      Response.isSucceed(userEstateApplications)
        ? userEstateApplications.result.filter(
            (ap) => 'AGREEENT_RENEWAL' != ap.source
          )
        : [],
    [userEstateApplications]
  );

  const { estates, action: getEstates } = useLoadEstatesHook();

  const router = useRouter();

  useEffect(() => {
    if (Response.isNotPerformed(userEstateApplications)) {
      getUserEstateApplications({
        page: 1,
      });
    } else if (Response.isSucceed(userEstateApplications)) {
      getEstates(
        visibleApplications.map(
          (estateApplication) => estateApplication.estateId
        )
      );
    }
  }, [userEstateApplications.type]);

  const handleResumeApplication = (estateLegalId: EstateLegalId) => {
    router.push(
      `/aplicar-inmueble/${estateLegalId.value}`.concat(
        UTMBuilder.build(constants.UTM_PARAMS)
      )
    );
  };

  const getApplicationListItem = (
    estate: EstateDetails | undefined,
    application: EstateApplication
  ) =>
    getApplicationListItemView({
      aplicationNumber: application.id.value,
      titleEstate: estate?.title || '',
      imgState: estate?.media ? getMainImage(estate.media)?.uri : '',
      key: application.estateId.value.toString(),
      result: estate
        ? EstateApplicationResultItemResult[
            getEstateApplicationResult(application.stage.kind)
          ]
        : EstateApplicationResultItemResult.UNAVAILABLE,
      action: () =>
        estate &&
        handleResumeApplication(
          new EstateLegalId(Number(estate.propertyLegalId))
        ),
    });

  return (
    <Container>
      <Head
        title={'Resultados de Solicitud de Arrendamiento | Aptuno'}
        description={
          'Conoce el resultado de tus solicitudes de arrendamiento y arrienda ya en Aptuno'
        }
        image={'https://www.aptuno.com/images/aptuno_logo.png'}
        url={'https://www.aptuno.com/aplicar-inmueble/resultados-aplicacion'}
      />
      <ContainerHeader>
        <Image
          src={'/images/flower1.svg'}
          width={82}
          height={32}
          alt="Resultados de Mis Solicitudes de Arrendamiento | Aptuno"
          title="Resultado de Solicitudes de Arrendamiento | Aptuno"
        />
        <Title>Tus solicitudes de arrendamiento</Title>
      </ContainerHeader>

      <ContainerData>
        {visibleApplications.map((application) => {
          const estate: EstateDetails | undefined = estates.find(
            (estate) => estate.id === application.estateId.value
          );
          return getApplicationListItem(estate, application);
        })}
      </ContainerData>
    </Container>
  );
};

const getApplicationListItemView = (item: EstateApplicationResultItem) => {
  return (
    <BoxActionCompact
      key={item.key}
      labelLeft={`Solicitud No. ${item.aplicationNumber}`}
      labelRight=""
      onClickLabelRight={() => {}}
      title={item.titleEstate}
      description={EstateApplicationResultItemResultTranslate[item.result]}
      descriptionAppearance={
        item.result === EstateApplicationResultItemResult.APPROVED
          ? 'green'
          : item.result === EstateApplicationResultItemResult.INCOMPLETE
          ? 'orange'
          : ''
      }
      textButton={
        item.result === EstateApplicationResultItemResult.APPROVED
          ? 'Reservar este inmueble'
          : item.result === EstateApplicationResultItemResult.INCOMPLETE
          ? 'Completar solicitud'
          : ''
      }
      onClickButton={() => item.action()}
      iconButton={
        item.result === EstateApplicationResultItemResult.APPROVED
          ? 'home'
          : item.result === EstateApplicationResultItemResult.INCOMPLETE
          ? 'application'
          : ''
      }
      srcImage={item.imgState}
      disabled={
        item.result === EstateApplicationResultItemResult.UNAVAILABLE
          ? true
          : false
      }
    />
  );
};
